// Self-contained "LOLER certificate" action: UPLOAD the certificate the examining body issued
// (ELE does not issue LOLER certificates). Owns its own modal + state. After a successful upload
// the row shows the new expiry + a Download link without a full reload, and calls onChanged so a
// host page (the lift detail page) can refresh the asset. Extracted from AssetsPage (Session AC)
// so it can be reused on the detail page.
import { useMemo, useState } from "react";
import { ApiError, api } from "../../../lib/api";
import type { Asset } from "../../../lib/resources";
import type { LolerUploadOutcome, UploadedLolerCertificate } from "../../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../../components/ModalShell";
import { Button, buttonClasses } from "../../../components/Button";
import { formatDate } from "../../../lib/format";

const LOLER_OUTCOMES: { value: LolerUploadOutcome; label: string }[] = [
  { value: "clear", label: "Clear (no defects)" },
  { value: "advisory", label: "Advisory" },
  { value: "immediate_defect", label: "Immediate defect (action required)" },
];

// Default next-due date: 6 months after a yyyy-mm-dd, clamped, as yyyy-mm-dd (UTC).
function sixMonthsOn(dateInput: string): string {
  const base = new Date(`${dateInput}T00:00:00Z`);
  if (Number.isNaN(base.getTime())) return "";
  const target = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 6, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  return new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(base.getUTCDate(), lastDay)))
    .toISOString()
    .slice(0, 10);
}

export function UploadCertButton({ asset, onChanged }: { asset: Asset; onChanged?: () => void }) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [inspectionDate, setInspectionDate] = useState(today);
  const [nextDueDate, setNextDueDate] = useState(() => sixMonthsOn(today));
  const [inspectorName, setInspectorName] = useState("ELTI (UK) Ltd");
  const [certificateRef, setCertificateRef] = useState("");
  const [outcome, setOutcome] = useState<LolerUploadOutcome>("clear");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadedLolerCertificate | null>(null);

  // Prefer the just-uploaded cert, else the asset's stored cert, for the row display.
  const certUrl = result?.lolerCertUrl ?? asset.lolerCertUrl ?? null;
  const certExpiry = result?.lolerCertExpiry ?? asset.lolerCertExpiry ?? null;

  function reset() {
    setFile(null);
    setInspectionDate(today);
    setNextDueDate(sixMonthsOn(today));
    setInspectorName("ELTI (UK) Ltd");
    setCertificateRef("");
    setOutcome("clear");
    setNotes("");
    setError(null);
    setResult(null);
  }

  async function upload() {
    if (!file) {
      setError("Attach the certificate PDF.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.assets.uploadLolerCertificate(asset.id, file, {
        inspectionDate: inspectionDate ? new Date(`${inspectionDate}T00:00:00Z`).toISOString() : undefined,
        nextDueDate: nextDueDate ? new Date(`${nextDueDate}T00:00:00Z`).toISOString() : undefined,
        inspectorName: inspectorName.trim() || undefined,
        certificateRef: certificateRef.trim() || undefined,
        outcome,
        notes: notes.trim() || undefined,
      });
      setResult(res);
      onChanged?.();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 503
          ? "Google Drive is not connected, so the certificate cannot be filed yet."
          : err instanceof ApiError && err.status === 415
            ? "The certificate must be a PDF."
            : "Could not upload the certificate. Please try again in a moment.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-1">
      {certUrl && certExpiry && (
        <div className="text-xs text-ele-body">
          Expires {formatDate(certExpiry)}{" "}
          <a
            href={api.assets.lolerCertificateHref(asset.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ele-green hover:underline"
          >
            Download
          </a>
        </div>
      )}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          reset();
          setOpen(true);
        }}
      >
        {certUrl ? "Update certificate" : "Upload certificate"}
      </Button>

      {open && (
        <ModalShell ariaLabel="Upload LOLER certificate" onClose={() => !busy && setOpen(false)} width="max-w-md" panelClassName="max-h-[90vh] overflow-y-auto p-6 text-left">
            <h2 className="mb-1 font-heading text-xl text-ele-strong">Upload LOLER certificate</h2>
            <p className="mb-4 text-sm text-ele-body">
              {asset.eleNumber ?? "Lift"}
              {asset.site?.name ? `, ${asset.site.name}` : ""}
            </p>

            {result ? (
              <div className="rounded-ele border border-ele-green bg-ele-green/10 p-4 text-sm">
                <p className="text-ele-strong">Certificate uploaded.</p>
                <p className="mt-1 text-ele-body">Next examination due {formatDate(result.lolerCertExpiry)}.</p>
                <a
                  href={api.assets.lolerCertificateHref(asset.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={buttonClasses("primary", "sm", "mt-3")}
                >
                  Download certificate
                </a>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-sm text-ele-body" htmlFor="loler-file">Certificate PDF</label>
                  <input
                    id="loler-file"
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    className="w-full text-sm text-ele-body file:mr-3 file:rounded-ele file:border-0 file:bg-ele-green file:px-3 file:py-1.5 file:text-ele-bg"
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm text-ele-body" htmlFor="loler-inspdate">Date of examination</label>
                    <input
                      id="loler-inspdate"
                      type="date"
                      value={inspectionDate}
                      onChange={(e) => {
                        setInspectionDate(e.target.value);
                        if (e.target.value) setNextDueDate(sixMonthsOn(e.target.value));
                      }}
                      className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-ele-body" htmlFor="loler-nextdue">Next due</label>
                    <input
                      id="loler-nextdue"
                      type="date"
                      value={nextDueDate}
                      onChange={(e) => setNextDueDate(e.target.value)}
                      className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-ele-body" htmlFor="loler-inspector">Examining body</label>
                  <input
                    id="loler-inspector"
                    type="text"
                    value={inspectorName}
                    onChange={(e) => setInspectorName(e.target.value)}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-ele-body" htmlFor="loler-ref">Certificate reference</label>
                  <input
                    id="loler-ref"
                    type="text"
                    value={certificateRef}
                    onChange={(e) => setCertificateRef(e.target.value)}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-ele-body" htmlFor="loler-outcome">Outcome</label>
                  <select
                    id="loler-outcome"
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value as LolerUploadOutcome)}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                  >
                    {LOLER_OUTCOMES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-ele-body" htmlFor="loler-notes">Notes (optional)</label>
                  <textarea
                    id="loler-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                  />
                </div>
                {error && <p role="alert" className="text-sm text-ele-danger">{error}</p>}
              </div>
            )}

            <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}
              >
                {result ? "Close" : "Cancel"}
              </button>
              {!result && (
                <Button size="sm" className={MODAL_ACTION_BTN} onClick={upload} disabled={busy || !file}>
                  {busy ? "Uploading..." : "Upload"}
                </Button>
              )}
            </div>
        </ModalShell>
      )}
    </div>
  );
}
